import React from 'react';
import {
  MessageSquare,
  FolderOpen,
  Mic,
  ChevronRight,
  FilePenLine,
  LayoutGrid,
  Users,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PortalHomeRecentItem, PortalView } from '../portalNavigation';
import { hasMatrixRoom } from '../portalNavigation';
import { PORTAL_SHELL } from '../portalTheme';
import { PortalLexcoreSnapshotBadge } from '../PortalLexcoreSnapshotBadge';
import { ARKO_HUB_MODES, dispatchArkoHubIntent, type ArkoHubMode } from '../arkoHubModes';

export interface HomeViewProps {
  customerName: string;
  kanzlei: string;
  isPremium: boolean;
  matrixRoomId?: string;
  recentItems: PortalHomeRecentItem[];
  onNavigate: (view: PortalView) => void;
  onRecentItemClick: (item: PortalHomeRecentItem) => void;
  onArkoModeSelect?: (mode: ArkoHubMode) => void;
}

const RECENT_ICON: Record<PortalHomeRecentItem['kind'], LucideIcon> = {
  document: FilePenLine,
  arko: MessageSquare,
  view: LayoutGrid,
};

export const HomeView: React.FC<HomeViewProps> = ({
  customerName,
  kanzlei,
  isPremium,
  matrixRoomId = '',
  recentItems,
  onNavigate,
  onRecentItemClick,
  onArkoModeSelect,
}) => {
  const firstName = customerName?.trim() || 'willkommen';
  const lastDocItem = recentItems.find((i) => i.kind === 'document');
  const matrixReady = hasMatrixRoom(matrixRoomId);

  const nextSteps: Array<{
    view: PortalView;
    label: string;
    description: string;
    icon: LucideIcon;
  }> = [
    {
      view: 'dokumente',
      label: 'Dokumente',
      description: 'Ordner anlegen, Dateien ablegen und im Editor mit ARKO bearbeiten.',
      icon: FolderOpen,
    },
    {
      view: 'diktat',
      label: 'Diktat',
      description: 'Mikrofon, Datei-Upload oder Element — Transkript und Protokoll folgen.',
      icon: Mic,
    },
    {
      view: 'matrix',
      label: 'Matrix',
      description: matrixReady
        ? 'Verschlüsselter Kanzlei-Raum — Sprachnachrichten und Protokolle über Element.'
        : 'Element-Raum wird beim Onboarding eingerichtet — bis dahin Diktat-Upload oder Anleitung.',
      icon: Users,
    },
  ];

  const quickActions = [
    {
      key: 'last',
      label: lastDocItem ? lastDocItem.label : 'Dokumente',
      sub: lastDocItem ? 'Zuletzt geöffnet' : 'Alle Dateien',
      icon: lastDocItem ? FilePenLine : FolderOpen,
      accent: 'text-blue-400 bg-blue-500/10 border-blue-500/20 hover:border-blue-500/40',
      onClick: () => {
        if (lastDocItem) onRecentItemClick(lastDocItem);
        else onNavigate('dokumente');
      },
      disabled: false,
    },
    {
      key: 'arko',
      label: 'ARKO',
      sub: 'Am geöffneten Dokument',
      icon: MessageSquare,
      accent: 'text-violet-300 bg-violet-500/10 border-violet-500/20 hover:border-violet-500/40 ring-1 ring-violet-400/10',
      onClick: () => onNavigate('dokumente'),
      disabled: false,
    },
    {
      key: 'diktat',
      label: 'Diktat',
      sub: 'Aufnehmen oder hochladen',
      icon: Mic,
      accent: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20 hover:border-emerald-500/40',
      onClick: () => onNavigate('diktat'),
      disabled: false,
    },
  ];

  return (
    <div className="space-y-8 animate-fade-in max-w-3xl mx-auto">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-violet-400" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-400/80">
              Orbis Arbeitsplatz
            </span>
          </div>
          <h1 className="text-xl font-semibold text-[#e6e7ea] tracking-tight">
            Guten Tag, <span className="text-slate-300">{firstName}</span>
          </h1>
          <p className={`${PORTAL_SHELL.textMuted} text-sm mt-1`}>{kanzlei}</p>
        </div>
        <PortalLexcoreSnapshotBadge size="sm" className="flex-shrink-0" />
      </header>

      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Geführte Workflows
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ARKO_HUB_MODES.map((mode) => {
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                type="button"
                onClick={() => {
                  onArkoModeSelect?.(mode);
                  dispatchArkoHubIntent(mode);
                }}
                className="group text-left rounded-xl border border-white/[0.08] bg-[#141619]/80 p-4 hover:bg-violet-500/[0.06] hover:border-violet-500/25 transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mb-3">
                  <Icon className="w-4 h-4 text-violet-300" aria-hidden />
                </div>
                <p className="text-sm text-white font-medium">{mode.label}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{mode.description}</p>
              </button>
            );
          })}
        </div>
        <p className={`${PORTAL_SHELL.textMuted} text-[11px] mt-3 leading-relaxed`}>
          Kein Chat für alles — wählen Sie einen Workflow oder nutzen Sie Modus, Dokument-Fokus und Chips im ARKO-Panel.{' '}
          <button
            type="button"
            onClick={() => onNavigate('chat')}
            className="text-violet-400/90 hover:text-violet-300 underline-offset-2 hover:underline"
          >
            ARKO fokussieren
          </button>
        </p>
      </section>

      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Nächste Schritte
        </h2>
        <p className={`${PORTAL_SHELL.textMuted} text-sm mb-3 leading-relaxed`}>
          Ihr Arbeitsplatz — alles an einem Ort: Assistent, Diktat, Dokumente und Matrix-Raum.
        </p>
        <ul className="space-y-2">
          {nextSteps.map(({ view, label, description, icon: Icon }) => (
            <li key={view}>
              <button
                type="button"
                onClick={() => onNavigate(view)}
                className="group w-full text-left rounded-xl border border-white/[0.08] bg-slate-900/40 p-3.5 hover:bg-white/[0.03] hover:border-white/[0.12] transition-colors flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-slate-400" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white font-medium">{label}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0 mt-1 opacity-0 group-hover:opacity-70 transition-opacity" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Schnellzugriff</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {quickActions.map(({ key, label, sub, icon: Icon, accent, onClick }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              className={`group text-left rounded-xl border p-4 transition-colors ${accent}`}
            >
              <Icon className="w-5 h-5 mb-3 opacity-90" aria-hidden />
              <p className="text-white text-sm font-medium truncate">{label}</p>
              <p className="text-slate-500 text-xs mt-0.5 truncate">{sub}</p>
              <ChevronRight className="w-4 h-4 mt-2 opacity-0 group-hover:opacity-60 transition-opacity" />
            </button>
          ))}
        </div>
      </section>

      {recentItems.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Zuletzt</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {recentItems.map((item) => {
              const Icon = RECENT_ICON[item.kind];
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onRecentItemClick(item)}
                  className="group text-left rounded-xl border border-white/[0.08] bg-slate-900/40 p-3.5 hover:bg-white/[0.03] hover:border-white/[0.12] transition-colors"
                >
                  <Icon className="w-4 h-4 text-slate-500 mb-2" aria-hidden />
                  <p className="text-sm text-slate-200 font-medium truncate">{item.label}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">{item.sublabel}</p>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {!isPremium && (
        <p className={`${PORTAL_SHELL.textMuted} text-xs leading-relaxed border-t border-white/[0.06] pt-4`}>
          Hinweis: ARKO unterstützt beim Formulieren und Zusammenfassen — keine Rechtsberatung.
        </p>
      )}
    </div>
  );
};
